import parse, { HTMLReactParserOptions } from 'html-react-parser';
import { useMemo } from 'react';

import { SanitizationProfileType, sanitizeHtml } from 'utils/sanitizeHtml';

import { dropInvalidInlineStyleAttributeProperties } from './utils';

type HtmlRendererProps = {
  /**
   * The html.
   *
   * This is typically unsafe in which case it will be sanitized based on the
   * sanitization profile.
   */
  html: string;
  /** The sanitation profile type of how to sanitize the html. */
  sanitizationProfileType: SanitizationProfileType;
  /**
   * A visitor function that allows you to replace a DOM element with another,
   * modification of its attributes or removal from the tree.
   *
   * The function visits every DOM node in the tree and replaces the node with
   * the returned react component.
   */
  replace?: HTMLReactParserOptions['replace'];
};

/**
 * Represents a component that safely parses and renders an html string as a
 * react component.
 */
export const HtmlRenderer = ({
  html,
  sanitizationProfileType,
  replace,
}: HtmlRendererProps): JSX.Element => {
  const htmlComponent = useMemo(() => {
    const sanitizedHtml = sanitizeHtml(html, sanitizationProfileType);

    return parse(sanitizedHtml, {
      replace: (domNode) =>
        replace
          ? replace(dropInvalidInlineStyleAttributeProperties(domNode))
          : dropInvalidInlineStyleAttributeProperties(domNode),
    });
  }, [html, sanitizationProfileType, replace]);

  return <>{htmlComponent}</>;
};
